import React, { useMemo } from 'react';
import PerformanceChart from './PerformanceChart';
import ClientReportingBenchmarkBar from './ClientReportingBenchmarkBar';
import ClientReportingDateRangeControls from './ClientReportingDateRangeControls';
import { IconInfo } from '../Icons';
import { CLIENT_REPORTING_BENCHMARKS } from './clientReportingBenchmarkConfig';
import { inceptionDate } from './clientReportingInception';
import { mergeBenchmarkSeriesIntoChartData } from './clientReportingBenchmarkMetrics';
import useClientReportingBenchmarks from './useClientReportingBenchmarks';

import {
  CLIENT_REPORTING_DEMO_CHART,
  CLIENT_REPORTING_DEMO_SERIES,
} from './clientReportingDemoPreview';

export default function ClientReportingChartPanel({
  chartTabs,
  chartTab,
  onChartTabChange,
  dateRange,
  onDateRangeChange,
  customDateRange,
  onCustomDateRangeChange,
  tabChartData,
  displaySeriesOrder,
  displayAccounts = [],
  chartValueFormat,
  effectiveRange,
  isChartInitialLoad,
  isChartRefreshing,
  qualityCaptions,
  compact = false,
  quotesBySymbol = {},
  quotesLoading = false,
  quotesWarning = '',
  seriesRaw = {},
  seriesOrder = [],
  youLabel = 'You',
  summariesById = {},
  showConnectPrompt = false,
  onAddAccount = null,
  connectPromptTitle = 'Add your account',
  connectPromptMessage = 'Connect a brokerage to see your equity curve.',
  connectPromptActionLabel = 'Add / Sync Trades',
  chartLineColors = null,
}) {
  // SnapTrade-linked accounts only have daily values from 12 months before linking.
  const linkedBrokers = [...new Set(displayAccounts
    .filter((a) => String(a?.dataSeriesName || '').startsWith('SNAPTRADE|'))
    .map((a) => a.broker || 'linked brokerage'))];
  const linkedHistoryNote = linkedBrokers.length
    ? `${linkedBrokers.join(', ')} history starts up to 12 months before the account was linked. That's as far back as SnapTrade provides daily account values. From then on, RiskLo keeps every day.`
    : null;

  // The benchmark hook charts seriesRaw[seriesOrder[0]], so the account whose
  // broker and summary feed the metrics has to be that same series. Matching by
  // name keeps them aligned — displayAccounts drops entries that don't resolve,
  // so indexing into it can silently pair series 0 with account 1.
  const primaryAccount = displayAccounts.find((account) => account?.name === seriesOrder[0]);
  const primarySeriesName = seriesOrder[0];
  const primarySeriesPoints = useMemo(
    () => seriesRaw?.[primarySeriesName] ?? [],
    [primarySeriesName, seriesRaw],
  );
  const overlayStartDate = useMemo(
    () => inceptionDate(primarySeriesPoints),
    [primarySeriesPoints],
  );
  const primaryBroker = primaryAccount?.broker || primaryAccount?.strategy || '';
  const realizedProfit = primaryAccount?.id != null
    ? summariesById[primaryAccount.id]?.profit?.realized ?? null
    : null;
  const netContributed = primaryAccount?.id != null
    ? summariesById[primaryAccount.id]?.equity?.investment ?? null
    : null;

  const {
    selectedBenchmarkIds,
    toggleBenchmark,
    benchmarkDataById,
    benchmarkMetricsById,
    youMetrics,
    youSummary,
    baseValue,
    loading: benchmarkLoading,
    warning: benchmarkWarning,
  } = useClientReportingBenchmarks({
    seriesRaw,
    seriesOrder,
    effectiveRange,
    primaryBroker,
    realizedProfit,
    netContributed,
  });

  const { data: chartData, seriesOrder: mergedSeriesOrder } = useMemo(
    () => mergeBenchmarkSeriesIntoChartData({
      chartData: tabChartData,
      seriesOrder: displaySeriesOrder,
      benchmarkDataById,
      selectedBenchmarkIds,
      chartTab,
      baseValue,
      benchmarks: CLIENT_REPORTING_BENCHMARKS,
      overlayStartDate,
      portfolioSeries: primarySeriesPoints,
    }),
    [
      tabChartData,
      displaySeriesOrder,
      benchmarkDataById,
      selectedBenchmarkIds,
      chartTab,
      baseValue,
      overlayStartDate,
      primarySeriesPoints,
    ],
  );

  const benchmarkSeriesNames = useMemo(
    () => new Set(
      CLIENT_REPORTING_BENCHMARKS
        .filter((benchmark) => selectedBenchmarkIds.has(benchmark.id))
        .map((benchmark) => benchmark.shortLabel),
    ),
    [selectedBenchmarkIds],
  );

  const connectPrompt = showConnectPrompt && onAddAccount
    ? {
      onAction: onAddAccount,
      title: connectPromptTitle,
      message: connectPromptMessage,
      actionLabel: connectPromptActionLabel,
      demoData: CLIENT_REPORTING_DEMO_CHART,
      demoSeries: [CLIENT_REPORTING_DEMO_SERIES],
    }
    : null;

  return (
    <section className={`vd-panel vd-client-report-panel vd-client-growth-panel vd-ml-chart-panel${compact ? ' vd-ml-chart-panel-compact' : ''}`}>
      <div className="vd-client-growth-control-row">
        <div className="vd-client-growth-control-cluster">
          <span className="vd-control-label">View</span>
          <div className="vd-toggle-group">
            {chartTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`vd-toggle ${chartTab === tab.id ? 'active' : ''}`}
                onClick={() => onChartTabChange(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <ClientReportingDateRangeControls
          dateRange={dateRange}
          onDateRangeChange={onDateRangeChange}
          customDateRange={customDateRange}
          onCustomDateRangeChange={onCustomDateRangeChange}
        />
      </div>

      <div className="vd-client-chart-canvas-wrap">
        <div className="vd-client-chart-info-anchor">
          <span className="vd-client-chart-info-wrap">
            <button
              type="button"
              className="vd-client-chart-info-btn"
              aria-label="How this Portfolio Tracker (Broker Sync) chart is calculated"
            >
              <IconInfo size={14} color="currentColor" />
            </button>
            <div className="vd-client-chart-info-tooltip" role="tooltip">
              Each line uses the best source available per broker. Alpaca and IBKR report true daily account value (NAV). Coinbase has no NAV API, so it is estimated from trades unless you import a transaction CSV (then it is exact). Tradovate / NinjaTrader futures use net liquidity when synced, rebased from closed-trade history plus the current Tradovate balance snapshot. On the Equity view, selected benchmarks start at your first funded balance and step up or down when you deposit or withdraw — then track the benchmark&apos;s daily returns on that capital (&quot;what if that cash went into SPY instead&quot;). Growth, Profit, and Drawdown compare performance with flows stripped out. Brokerages linked through SnapTrade (Schwab, Robinhood, Webull…) report true daily account value, going back up to 12 months before they were linked (SnapTrade&apos;s limit); RiskLo keeps every day after that.
            </div>
          </span>
        </div>

        <div className="vd-client-chart-stack">
          <PerformanceChart
            data={chartData}
            series={mergedSeriesOrder}
            seriesAccounts={displayAccounts}
            valueFormat={chartValueFormat}
            dateRange={effectiveRange}
            isInitialLoad={isChartInitialLoad}
            isRefreshing={isChartRefreshing}
            dashedSeriesNames={benchmarkSeriesNames}
            showHeadlineValue={chartValueFormat === 'currency'}
            compact={compact}
            /* Profit reads as money made or lost, so its fill is green rather
               than the account's series color. Equity keeps the account
               color — it is a balance, not a result. */
            areaTone={chartTab === 'profit' ? 'green' : null}
            connectPrompt={connectPrompt}
            chartLineColors={chartLineColors}
          />
        </div>
      </div>

      {linkedHistoryNote ? (
        <p className="vd-chart-history-note">
          <IconInfo size={13} color="currentColor" />
          <span>{linkedHistoryNote}</span>
        </p>
      ) : null}

      <ClientReportingBenchmarkBar
        quotesBySymbol={quotesBySymbol}
        quotesLoading={quotesLoading}
        quotesWarning={quotesWarning}
        benchmarkMetricsById={benchmarkMetricsById}
        youMetrics={youMetrics}
        youSummary={youSummary}
        selectedBenchmarkIds={selectedBenchmarkIds}
        onToggleBenchmark={toggleBenchmark}
        benchmarkLoading={benchmarkLoading}
        benchmarkWarning={benchmarkWarning}
        youLabel={youLabel}
      />

      {qualityCaptions.length > 0 && (
        <div className="vd-data-quality">
          {qualityCaptions.map(({ name, quality }) => (
            <span key={name} className={`vd-data-quality-item quality-${quality.quality}`}>
              <span className="vd-data-quality-dot" />
              <span className="vd-data-quality-name">{name}</span>
              <span className="vd-data-quality-label">{quality.label}</span>
            </span>
          ))}
        </div>
      )}

      <div className="vd-client-chart-footnote">
        Account curves use the most accurate source available per broker: actual NAV from the broker or an imported statement when present, otherwise an estimate reconstructed from trades.
      </div>
    </section>
  );
}
